import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck, Server, Database, Cloud, Activity, Check, ArrowUpRight,
} from "lucide-react";
import { SERVICES } from "../data/content";
import { useModals } from "./modals/ModalProvider";

const iconMap = {
  "shield-check": ShieldCheck,
  server: Server,
  database: Database,
  cloud: Cloud,
  activity: Activity,
};

// Vibrant tone tokens (uses brand palette + accent)
const toneMap = {
  primary: { grad: "from-primary via-primary to-[#0a3560]", chip: "bg-primary/10 text-primary", ring: "shadow-primary/25" },
  secondary: { grad: "from-secondary via-[#ea580c] to-[#c2410c]", chip: "bg-secondary/10 text-secondary", ring: "shadow-secondary/25" },
  accent: { grad: "from-accent via-[#0EA5E9] to-primary", chip: "bg-accent/10 text-primary", ring: "shadow-accent/25" },
};

export default function ServicesGrid() {
  const { openBookConsultation } = useModals();
  return (
    <section id="services" className="py-20 sm:py-28 relative bg-gradient-to-b from-background via-muted/40 to-background dark:via-muted/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-secondary">What we deliver</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            Five focused practices. One <span className="text-gradient-brand">integrated outcome.</span>
          </h2>
          <p className="mt-4 text-muted-foreground text-base sm:text-lg leading-relaxed">
            Strategy, architecture, delivery and managed operations — sequenced around your business risk and
            compliance obligations.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = iconMap[s.icon] || ShieldCheck;
            const tone = toneMap[s.tone] || toneMap.primary;
            // Bento sizing: cards 1&4 span 6, cards 2&3 span 6, card 5 spans 12
            const span = i === 4 ? "lg:col-span-12" : "lg:col-span-6";
            return (
              <motion.article
                key={s.id}
                id={`service-${s.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                data-testid={`service-card-${s.id}`}
                className={`card-lift group scroll-mt-28 relative overflow-hidden rounded-3xl border border-border bg-card p-6 sm:p-8 ${span}`}
              >
                {/* Decorative gradient blob */}
                <div className={`absolute -top-16 -right-16 h-48 w-48 rounded-full bg-gradient-to-br ${tone.grad} opacity-10 group-hover:opacity-20 blur-2xl transition-opacity`} />
                <div className="relative flex items-start gap-4">
                  <div className={`h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-gradient-to-br ${tone.grad} text-white grid place-items-center shrink-0 shadow-lg ${tone.ring}`}>
                    <Icon className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={1.6} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight">{s.title}</h3>
                      <ArrowUpRight className="h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    </div>
                    <p className={`mt-1 inline-flex text-[11px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ${tone.chip}`}>
                      {s.tagline}
                    </p>
                    <p className="mt-3 text-sm sm:text-[15px] leading-relaxed text-muted-foreground">
                      {s.desc}
                    </p>
                    <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                      {s.highlights.map((h) => (
                        <li key={h} className="flex items-start gap-2 text-[13px] text-foreground/85">
                          <div className={`mt-1 h-4 w-4 rounded-full ${tone.chip} grid place-items-center shrink-0`}>
                            <Check className="h-2.5 w-2.5" strokeWidth={3} />
                          </div>
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={openBookConsultation}
                      data-testid={`service-cta-${s.id}`}
                      className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80"
                    >
                      Discuss with a consultant <ArrowUpRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
